import React, { useCallback, useEffect, useMemo } from 'react'
import './dashboard.css'
import { Container } from '@sencha/ext-modern'
import { DashboardLayoutComponent } from '@syncfusion/ej2-react-layouts'
import { useDelayTimer, useInstData } from 'tyan/public/hooks'
import { isEqual } from 'lodash'

const containerPadding = 10

const DashboradComp = props => {
	const {
		name: dashboardName = 'dashboard', 	// 名称，用于保存布局
		columns = 10,				// 列数，默认为10
		rowHeight,				// 行高，有此值则rows无效；主要用于内容高度相对固定的情况(px)
		rows = 30,				// 行数，默认为30，用于计算ratio
		spacing = 5,			// 间隔大小(px)，默认为5
		reset = '重置主页', 	// 重置主页按钮文本，转为''、null或false则不显示
		items = [],					// 元素内容对象数据: id, x, y, width, height, title, renderer
		containerProps = {}		// extjs Container 属性
	} = props

	const { me, setState, me: {
		ratio = 1, dashboardID,
		state: { renderMe, created, refreshStamp }
	} } = useInstData(props, useMemo(() => ({
		dashboardID: `dashboard_${Tyan.getUuid()}`
	}), []))

	const layoutInfoKey = useMemo(() => {
		const { userName, subOrg } = Tyan.loginInfo || {}
		return `${dashboardName}${userName ? `_${userName}` : ''}${subOrg ? `_${subOrg}` : ''}`
	}, [dashboardName])

	// 根据me.container计算ratio, cellHeight
	const getRatioAndHeight = useCallback(() => {
		let width = 1640, height = 858
		if (me.container) {
			const dom = me.container.innerElement.dom
			// const styles = dom.computedStyleMap()
			// width = dom.clientWidth - styles.get('padding-left').value - styles.get('padding-right').value
			// height = dom.clientHeight - styles.get('padding-top').value - styles.get('padding-bottom').value
			width = dom.clientWidth - containerPadding * 2
			height = dom.clientHeight - containerPadding * 2
		}

		const cellHeight = rowHeight || ((height - spacing * (rows - 1)) / rows)
		const cellWidth = me.dashboardObj ? me.dashboardObj.cellSize[0] : ((width - spacing * (columns - 1)) / columns)
		const ratio = cellWidth / cellHeight
		// console.log('ratio and cellHeight:', ratio, cellHeight)
		return { ratio, cellHeight }
	}, [rowHeight])

	// 重新计算ratio后刷新
	const handleContainerResize = (() => {
		const { doDelay: doDelayRefresh } = useDelayTimer(200)
		return useMemo(() => {
			const doRefresh = () => {
				const obj = me.dashboardObj
				if (!obj) {
					doDelayRefresh(doRefresh)
					return
				}
				const { ratio, cellHeight } = getRatioAndHeight()
				obj.cellAspectRatio = ratio
				obj.refresh()
				if (Math.abs(obj.cellSize[1] - cellHeight) > 0.01) {
					obj.cellAspectRatio = obj.cellSize[0] / cellHeight
					obj.refresh()
				}
			}

			return (...args) => {
				doDelayRefresh(doRefresh)
			}
		}, [rowHeight, rows, columns])
	})()

	// 保存布局
	const saveLayout = (() => {
		const { doDelay: delaySaveLayout } = useDelayTimer(200)
		return useMemo(() => {
			const doSaveLayout = () => {
				localStorage.setItem(layoutInfoKey, JSON.stringify(me.dashboardObj.serialize()))
			}

			return () => delaySaveLayout(doSaveLayout)
		}, [])
	})()

	// Dashboard Layout's created event function
	const handleCreated = useMemo(() => {
		return () => {
			const obj = me.dashboardObj
			// obj.cellAspectRatio = obj.cellSize[0] / CELLHEIGHT
			obj.refresh()
			setState({ created: true })
		}
	}, [])

	//Dashboard Layout's change event function
	const handleChange = args => {
		// console.log('on change:', args)
		args.isInteracted && saveLayout()
		// saveLayout()
	}

	const handlePanelResize = args => {
		// console.log('panelResize:', args)
		saveLayout()
	}

	useEffect(() => {
		const showMe = () => {
			if (!(me.containerObj && me.containerObj.clientWidth)) {
				setTimeout(showMe)
				return
			}
			const { ratio } = getRatioAndHeight()
			me.ratio = ratio
			setState({ renderMe: true })
		}
		showMe()
	}, [])

	// 重置主页按钮
	useEffect(() => {
		const id = reset && Tyan.breadcrumbsButtons.add({
			_button: reset, ui: '', handler: () => (localStorage.removeItem(layoutInfoKey), setState({ refreshStamp: Date.now() }))
		})

		return () => {
			id && Tyan.breadcrumbsButtons.remove(id)
			me.dashboardObj && me.dashboardObj.destroy()
		}

	}, [])

	// 初始或数据变动时重置panels
	useEffect(() => {
		if (!created || !items)
			return

		// 读取已经保存的布局
		const savedLayoutArr = JSON.parse(localStorage.getItem(layoutInfoKey) || "[]")

		// 生成panels对象(id为键)
		const panelsObj = items.reduce((sum, { id, x, y, width, height, title, renderer }) => {
			// console.log('renderer:', renderer)
			const panel = {
				id: `pnl_${id}`,
				sizeX: width,
				sizeY: height,
				minSizeY: 2,
				row: y,
				col: x,
				header: title && typeof title !== 'function' ? `<div style='text-align: center;color: white;padding-top: 3px;'>${title}</div>` : title,
				content: renderer
			}
			sum[panel.id] = panel
			return sum
		}, {})

		// id数组未发生改变，置布局为已保存内容
		const changed = !isEqual(savedLayoutArr.map(o => o.id).sort(), Object.keys(panelsObj).sort())
		!changed && savedLayoutArr.forEach(layout => {
			const panel = panelsObj[layout.id]
			Object.entries(layout).forEach(([k, v]) => panel[k] = v)
		})

		const panels = Object.values(panelsObj)

		me.dashboardObj.panels = panels
		changed && saveLayout()
	}, [items, created, refreshStamp])

	const cellSpacing = [spacing, spacing]

	return (<Container id={dashboardID} ref={ref => { ref && (me.container = ref.cmp) }}
		padding={containerPadding} scrollable={true}
		onResize={handleContainerResize}
		{...containerProps}
	>
		<div className='t-dashboard-wrapper' style={{ flex: 1 }}>
			<div className="t-container" ref={ref => ref && (me.containerObj = ref)}>
				{renderMe && <DashboardLayoutComponent ref={ref => { ref && (me.dashboardObj = ref) }}
					allowResizing={true} draggableHandle='.e-panel-header'
					cellSpacing={cellSpacing} columns={columns} cellAspectRatio={ratio} mediaQuery='max-width: 600px'
					/* panels={[]} */ created={handleCreated} change={handleChange} resizeStop={handlePanelResize} /> || null}
			</div>
		</div>
	</Container >)

}

export default DashboradComp